import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useWebSocket } from "@/hooks/use-websocket";
import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import { Loader2, ChevronDown, MoreVertical, LogOut, UserX, Trash2, Lock } from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import { CreateOrganisationModal } from "@/components/create-organisation-modal";
import { CreateUserModal } from "@/components/create-user-modal";
import { AllocateUserToOrgModal } from "@/components/allocate-user-to-org-modal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { UserTable } from "@/components/user-table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { useToast } from "@/hooks/use-toast";
import { OrganisationPanel } from "@/components/organisation-panel";
import { GlassPanel } from "@/components/ui/glass-panel";
import { PageBackground } from "@/components/ui/page-background";
import { ResetPasswordModal } from "@/components/reset-password-modal";
import { AddLogoModal } from "@/components/add-logo-modal";
import { UserActionsDropdown } from "@/components/user-actions-dropdown";

async function fetchOrganisations() {
  const response = await fetch("/api/organisations");
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch organisations. Server responded with status ${response.status}.`);
  }
  
  // Clone the response to be able to read the body twice in case of error
  const clonedResponse = response.clone();
  try {
    return await response.json();
  } catch (e) {
    const errorText = await clonedResponse.text();
    // Re-throw the original parsing error to be caught by useQuery
    throw e;
  }
}

async function deleteOrganisation(orgId: number) {
  const response = await fetch(`/api/organisations/${orgId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to delete organisation");
  }
}

async function removeUserFromOrg({ orgId, userId }: { orgId: number, userId: number }) {
  const response = await fetch(`/api/organisations/${orgId}/users/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to remove user");
  }
}

async function forceLogoutUser(userId: number) {
  const response = await fetch(`/api/users/${userId}/force-logout`, {
    method: 'POST',
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to logout user");
  }
  return response.json();
}

async function fetchAllUsers() {
  const response = await fetch("/api/users");
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch users. Server responded with status ${response.status}: ${errorText}`);
  }
  return response.json();
}

async function deleteUser(userId: number) {
  const response = await fetch(`/api/users/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to delete user");
  }
}

export function OrganisationsPage() {
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuth();
  useWebSocket(); // Establish WebSocket connection
  
  const [, setLocation] = useLocation();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isCreateUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [isAllocateUserModalOpen, setAllocateUserModalOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [userToRemove, setUserToRemove] = useState<{ orgId: number; userId: number; username: string } | null>(null);
  const [orgToDelete, setOrgToDelete] = useState<{ id: number; name: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [userToDelete, setUserToDelete] = useState<{ id: number; username: string } | null>(null);
  const [usersPanelOpen, setUsersPanelOpen] = useState(false);
  const [userPanelSearch, setUserPanelSearch] = useState("");
  const [resetPasswordUser, setResetPasswordUser] = useState<{ id: number; username: string } | null>(null);
  const [logoModalOrg, setLogoModalOrg] = useState<{ id: number; name: string; logoUrl?: string | null } | null>(null);
  
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Helper: is god
  const isGod = user?.role === 'god';
  // Helper: orgs where user is owner
  const ownerOrgIds = user?.organisationUsers?.filter(ou => ou.role === 'owner').map(ou => ou.organisation.id) || [];
  const isOwner = ownerOrgIds.length > 0;
  // Helper: orgs where user is owner (full org objects)
  const ownerOrgs = user?.organisationUsers?.filter((ou: any) => ownerOrgIds.includes(ou.organisation.id)).map((ou: any) => ({ id: ou.organisation.id, name: ou.organisation.name })) || [];

  // Access control: only god or owner of at least one org
  useEffect(() => {
    if (!isAuthLoading && (!isAuthenticated || (!isGod && !isOwner))) {
      setLocation("/");
    }
  }, [isAuthLoading, isAuthenticated, user, setLocation, isGod, isOwner]);

  // WebSocket automatically handles organization membership changes through query invalidation
  // No manual message handling needed - the useWebSocket hook will invalidate queries automatically

  // Fetch orgs for god or owner
  const { data: organisations, isLoading: isOrgsLoading, error } = useQuery({
    queryKey: ["organisations"],
    queryFn: fetchOrganisations,
    enabled: !isAuthLoading && isAuthenticated && (isGod || isOwner),
  });

  // Filter orgs: god sees all, owner sees only their orgs
  const visibleOrganisations = isGod
    ? organisations
    : organisations?.filter((org: any) => ownerOrgIds.includes(org.id));

  const filteredOrganisations = visibleOrganisations?.filter((org: any) =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const deleteOrgMutation = useMutation({
    mutationFn: deleteOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast({ title: "Organisation deleted", description: "The organisation and all its users and events have been deleted." });
      setOrgToDelete(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: removeUserFromOrg,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast({ title: "User removed", description: "The user has been removed from the organisation." });
      setUserToRemove(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const logoutUserMutation = useMutation({
    mutationFn: forceLogoutUser,
    onSuccess: (data) => {
      toast({ title: "Success", description: data.message });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleAllocateUserClick = (orgId: number) => {
    setSelectedOrgId(orgId);
    setAllocateUserModalOpen(true);
  };

  // Fetch all users (god only)
  const { data: allUsers, isLoading: isUsersLoading, error: usersError } = useQuery({
    queryKey: ["all-users"],
    queryFn: fetchAllUsers,
    enabled: isGod,
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-users"] });
      toast({ title: "User deleted", description: "The user has been deleted." });
      setUserToDelete(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const forceLogoutMutation = useMutation({
    mutationFn: forceLogoutUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast({
        description: "User has been logged out successfully."
      });
    },
    onError: (error: Error) => {
      toast({
        description: error.message || "Failed to logout user",
        variant: "destructive",
      });
    },
  });

  const toggleArtistManagementMutation = useMutation({
    mutationFn: async ({ orgId, enabled }: { orgId: number; enabled: boolean }) => {
      const response = await fetch(`/api/organisations/${orgId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artistManagement: enabled }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update artist management setting");
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast({
        title: "Settings Updated",
        description: `Artist management has been ${variables.enabled ? 'enabled' : 'disabled'}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update settings",
        variant: "destructive",
      });
    },
  });

  if (isAuthLoading || isOrgsLoading) {
    return (
      <PageBackground className="flex items-center justify-center">
        <div className="text-center relative z-10">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-slate-700 drop-shadow-sm" />
          <p className="text-slate-700 drop-shadow-sm">Loading...</p>
        </div>
      </PageBackground>
    );
  }

  if (error) {
    return <div>Error fetching organisations: {error.message}</div>;
  }

  return (
    <PageBackground>
      <AppHeader />
      <main className="p-4 md:p-8 relative z-10">
        {/* Owner Create User Button (not god) */}
        {!isGod && isOwner && (
          <div className="mb-4">
            <Button 
              className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
              style={{
                boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
              onClick={() => setCreateUserModalOpen(true)}
            >
              Create User
            </Button>
          </div>
        )}
        {/* Users Panel (God only) */}
        {isGod && (
          <GlassPanel className="mb-4">
            <Collapsible open={usersPanelOpen} onOpenChange={setUsersPanelOpen}>
              <div className="p-6 border-b border-white/20 relative z-10">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900/90 drop-shadow-sm">Users</h3>
                    <p className="text-slate-600/80 mt-1 drop-shadow-sm">Manage all users in the system</p>
                  </div>
                  <CollapsibleTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-600/80 hover:bg-white/20 hover:text-slate-700 transition-all duration-300"
                    >
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 drop-shadow-sm ${usersPanelOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                </div>
              </div>
              <CollapsibleContent className="p-6 pt-0 relative z-10">
                <div className="flex items-center gap-2 mb-4 pt-4">
                  <Input
                    placeholder="Search users..."
                    value={userPanelSearch}
                    onChange={(e) => setUserPanelSearch(e.target.value)}
                    className="max-w-sm"
                  />
                  {userPanelSearch && (
                    <Button 
                      variant="outline" 
                      onClick={() => setUserPanelSearch("")}
                      className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                      style={{
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                      }}
                    >
                      Clear
                    </Button>
                  )}
                  <Button 
                    className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
                    style={{
                      boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                    onClick={() => setCreateUserModalOpen(true)}
                  >
                    Create User
                  </Button>
                </div>
                <UserTable
                  users={allUsers?.filter((u: any) => u.username.toLowerCase().includes(userPanelSearch.toLowerCase())) || []}
                  isLoading={isUsersLoading}
                  error={usersError}
                  renderActions={(u: any) => (
                    <UserActionsDropdown
                      user={u}
                      onResetPassword={(id, username) => setResetPasswordUser({ id, username })}
                      onForceLogout={isGod ? (id) => forceLogoutMutation.mutate(id) : undefined}
                      onDelete={(id, username) => setUserToDelete({ id, username })}
                      showForceLogout={isGod}
                    />
                  )}
                />
              </CollapsibleContent>
            </Collapsible>
          </GlassPanel>
        )}

        <div className="flex items-center gap-2 mb-4">
            <Input
                placeholder="Search organisations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
            />
            {searchTerm && (
                <Button 
                  variant="outline" 
                  onClick={() => setSearchTerm("")}
                  className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  Clear
                </Button>
            )}
            {isGod && (
              <Button 
                className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
                style={{
                  boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
                onClick={() => setCreateModalOpen(true)}
              >
                Create Organisation
              </Button>
            )}
        </div>
        <CreateOrganisationModal isOpen={isCreateModalOpen} onOpenChange={setCreateModalOpen} />
        <CreateUserModal isOpen={isCreateUserModalOpen} onOpenChange={setCreateUserModalOpen} isGod={isGod} ownerOrgIds={ownerOrgIds} ownerOrgs={ownerOrgs} />
        {isGod && selectedOrgId && (
            <AllocateUserToOrgModal
                isOpen={isAllocateUserModalOpen}
                onOpenChange={setAllocateUserModalOpen}
                organisationId={selectedOrgId}
            />
        )}

        <div className="space-y-4">
            {filteredOrganisations?.map((org: any) => (
                <OrganisationPanel
                  key={org.id}
                  org={org}
                  isGod={isGod}
                  isOwner={ownerOrgIds.includes(org.id)}
                  onDeleteOrg={(id, name) => setOrgToDelete({ id, name })}
                  onAllocateUser={handleAllocateUserClick}
                  onRemoveUser={(orgId, userId, username) => setUserToRemove({ orgId, userId, username })}
                  onForceLogout={(userId) => logoutUserMutation.mutate(userId)}
                  onResetPassword={(userId, username) => setResetPasswordUser({ id: userId, username })}
                  onAddLogo={(id, name) => setLogoModalOrg({ id, name, logoUrl: org.logoUrl })}
                  onToggleArtistManagement={isGod ? (orgId, enabled) => toggleArtistManagementMutation.mutate({ orgId, enabled }) : undefined}
                />
            ))}
        </div>
        <ConfirmationDialog
          isOpen={!!userToRemove}
          onOpenChange={() => setUserToRemove(null)}
          title="Are you sure?"
          description={`This action will permanently remove ${userToRemove?.username} from the organisation and log them out. This cannot be undone.`}
          onConfirm={() => userToRemove && removeUserMutation.mutate({ orgId: userToRemove.orgId, userId: userToRemove.userId })}
          confirmText={removeUserMutation.isPending ? "Removing..." : "Remove"}
          isConfirming={removeUserMutation.isPending}
        />
        <ConfirmationDialog
          isOpen={!!orgToDelete}
          onOpenChange={() => setOrgToDelete(null)}
          title="Are you sure?"
          description={`This will permanently delete the "${orgToDelete?.name}" organisation and all of its associated users and events. This action cannot be undone.`}
          onConfirm={() => orgToDelete && deleteOrgMutation.mutate(orgToDelete.id)}
          confirmText={deleteOrgMutation.isPending ? "Deleting..." : "Delete"}
          isConfirming={deleteOrgMutation.isPending}
          confirmVariant="destructive"
        />
        <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the user "{userToDelete?.username}". This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => userToDelete && deleteUserMutation.mutate(userToDelete.id)}>
                {deleteUserMutation.isPending ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        <ResetPasswordModal
          isOpen={!!resetPasswordUser}
          onOpenChange={(open) => !open && setResetPasswordUser(null)}
          userId={resetPasswordUser?.id}
          username={resetPasswordUser?.username}
          isOwnPassword={false}
        />
        
        <AddLogoModal
          isOpen={!!logoModalOrg}
          onOpenChange={(open) => !open && setLogoModalOrg(null)}
          organisationId={logoModalOrg?.id || 0}
          organisationName={logoModalOrg?.name || ""}
          currentLogoUrl={logoModalOrg?.logoUrl}
        />
      </main>
    </PageBackground>
  );
}

export default OrganisationsPage; 